// Packages and components
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box } from 'grommet';
import { FormClose, FormPrevious } from 'grommet-icons';
import {
  StyledLayer,
  ModalControls,
  ModalContainer,
  ModalBody,
} from './SHDModal.styles';

// Helpers and utilities
import { getComponentClassName } from '@theme/utils/data';
import { ResponsiveContext } from 'grommet';

// Types and enums
import type { SHDModalProps } from './SHDModal.d';

const SHDModal: React.FC<SHDModalProps> = ({
  children,
  className,
  isFixedHeight = false,
  isRelativeHeight = false,
  isConfirmation = false,
  isFixedWidth = false,
  onClose,
  onGoBack,
}) => {
  // Constants
  const componentClassName = 'modal';

  // States
  const [utilityClassNames, setUtilityClassNames] = useState<string[]>();
  const [fill, setFill] = useState<boolean | 'vertical'>('vertical');

  // Hooks and contexts
  const viewportSize = useContext(ResponsiveContext);

  // Handlers
  const handleUtilityClassNameUpdates = useCallback(() => {
    const utilityClassArr: string[] = [];
    if (className) {
      className.split(' ').forEach((name) => {
        utilityClassArr.push(name);
      });
    }

    if (isFixedHeight) {
      utilityClassArr.push('is-height-fixed');
    }

    if (isRelativeHeight) {
      utilityClassArr.push('is-height-relative');
    }

    if (isConfirmation) {
      utilityClassArr.push('is-confirmation');
    }

    if (isFixedWidth) {
      utilityClassArr.push('is-width-fixed');
    }

    utilityClassArr.push(`is-${viewportSize}`);
    setUtilityClassNames(utilityClassArr);
  }, [
    className,
    isConfirmation,
    isFixedHeight,
    isRelativeHeight,
    viewportSize,
    isFixedWidth,
  ]);

  const handlePropUpdates = useCallback(() => {
    if (viewportSize === 'small' || viewportSize === 'medium') {
      setFill('vertical');
    }

    if (viewportSize === 'large' || viewportSize === 'xlarge') {
      setFill(false);
    }

    if (isFixedHeight || isRelativeHeight) {
      setFill(false);
    }
  }, [isFixedHeight, isRelativeHeight, viewportSize]);

  // Effects
  useEffect(() => {
    handleUtilityClassNameUpdates();
    handlePropUpdates();
  }, [handleUtilityClassNameUpdates, handlePropUpdates]);

  return (
    <StyledLayer
      className={getComponentClassName(componentClassName, utilityClassNames)}
      full={fill}
      onClickOutside={onClose}
      onEsc={onClose}
      responsive={true}
    >
      <ModalContainer
        className={getComponentClassName(
          `${componentClassName}__container`,
          utilityClassNames
        )}
        fill={fill}
        gap="small"
        overflow={{ vertical: 'auto', horizontal: 'hidden' }}
        width="large"
      >
        <ModalControls
          align="center"
          className={getComponentClassName(
            `${componentClassName}__controls`,
            utilityClassNames
          )}
          direction="row"
          justify="between"
        >
          <Box
            flex={{ grow: 0 }}
            align="start"
            style={{ position: 'relative', transform: 'translateX(-4px)' }}
          >
            {onGoBack && (
              <Box
                aria-label="Go Back"
                role="button"
                onClick={onGoBack}
                pad={{ horizontal: '1rem', vertical: '0.875rem' }}
              >
                <FormPrevious size="30px" color="black" />
              </Box>
            )}
          </Box>
          <Box flex={{ grow: 0 }} align="end">
            {onClose && (
              <Box
                aria-label="Close Modal"
                role="button"
                onClick={onClose}
                pad={{ horizontal: '1rem', vertical: '0.875rem' }}
              >
                <FormClose size="30px" color="black" />
              </Box>
            )}
          </Box>
        </ModalControls>
        <ModalBody
          className={getComponentClassName(
            `${componentClassName}__content`,
            utilityClassNames
          )}
          flex="grow"
        >
          {children}
        </ModalBody>
      </ModalContainer>
    </StyledLayer>
  );
};

export default SHDModal;
